// Permissions Service - Handles permission management API calls

import { apiGet, apiPatch } from '@/utils/api';
import { Permission, UserRole } from '@/types/global.types';

// Types for API responses
export interface PermissionsByCategory {
  student: Permission[];
  staff: Permission[];
  grades: Permission[];
  payments: Permission[];
  reports: Permission[]; // Includes E-DEP permissions
  settings: Permission[];
  permission: Permission[];
}

export interface StaffPermissionsResponse {
  userId: string;
  role: UserRole;
  roleNameFr: string;
  rolePermissions: Permission[];
  effectivePermissions: Permission[];
  overrides: { permission: Permission; granted: boolean }[];
  defaultPermissions: Permission[];
  availablePermissions: PermissionsByCategory;
  blockedPermissions: Permission[]; // Permissions that are permanently blocked for this role
}

export interface MyPermissionsResponse {
  userId: string;
  role: UserRole;
  permissions: Permission[];
}

/**
 * Fetch current user's permissions from API
 */
export async function fetchMyPermissions(): Promise<MyPermissionsResponse> {
  return apiGet<MyPermissionsResponse>('/permissions/user/me');
}

/**
 * Fetch all available permissions grouped by category
 */
export async function fetchAvailablePermissions(): Promise<{
  all: Permission[];
  byCategory: PermissionsByCategory;
}> {
  return apiGet<{ all: Permission[]; byCategory: PermissionsByCategory }>('/permissions/available');
}

/**
 * Fetch a staff member's permissions for editing
 */
export async function fetchStaffPermissions(userId: string): Promise<StaffPermissionsResponse> {
  return apiGet<StaffPermissionsResponse>(`/permissions/staff/${userId}`);
}

/**
 * Update a staff member's permissions
 */
export async function updateStaffPermissions(
  userId: string,
  permissions: Permission[]
): Promise<{ success: boolean; message: string }> {
  return apiPatch<{ success: boolean; message: string }>(`/permissions/staff/${userId}`, { permissions });
}

/**
 * Check if current user can manage a specific staff member's permissions
 */
export async function canManageStaffPermissions(userId: string): Promise<{
  targetUserId: string;
  canManage: boolean;
}> {
  return apiGet<{ targetUserId: string; canManage: boolean }>(`/permissions/staff/${userId}/can-manage`);
}

/**
 * Get default permissions for a specific role
 */
export async function fetchDefaultPermissionsForRole(role: UserRole): Promise<{
  role: UserRole;
  defaultPermissions: Permission[];
}> {
  return apiGet<{ role: UserRole; defaultPermissions: Permission[] }>(`/permissions/defaults/${role}`);
}

/**
 * Check if current user has a specific permission
 */
export async function checkPermission(permission: Permission): Promise<{
  permission: Permission;
  hasPermission: boolean;
}> {
  return apiGet<{ permission: Permission; hasPermission: boolean }>(`/permissions/check/${permission}`);
}

// Export all functions as a service object for convenience
export const permissionsService = {
  fetchMyPermissions,
  fetchAvailablePermissions,
  fetchStaffPermissions,
  updateStaffPermissions,
  canManageStaffPermissions,
  fetchDefaultPermissionsForRole,
  checkPermission,
};

export default permissionsService;
